import React from 'react';

import { DefaultContext } from '@apollo/client';

import { useAuthCredentials } from '@hrlogics/react-auth';

import { Timezone } from '@clearverify/constants';
import { Nullable } from '@clearverify/types';

import type { UserUpdate } from 'src/api';
import { useCurrentUserQuery, useUpdateUserMutation } from 'src/api';
import { getUserFullName } from 'src/utils/user';

export const useCurrentUser = () => {
  const { isAuthorized } = useAuthCredentials();

  const { data, loading: isUserLoading } = useCurrentUserQuery({
    skip: !isAuthorized,
  });

  const [updateUser, { loading: isUserUpdating }] = useUpdateUserMutation();

  const user = React.useMemo(() => data?.currentUser, [data?.currentUser]);

  const id = React.useMemo(() => user?.id, [user?.id]);
  const timezone = React.useMemo(() => user?.timezone as Nullable<Timezone>, [user]);

  const userFullName = React.useMemo(() => getUserFullName(user), [user]);

  const isPartnershipCompanyUser = React.useMemo(() => {
    return (
      !!user?.peoStaff?.isPartnershipCompany ||
      !!user?.peoPayrollProviderStaff?.isPartnershipCompany
    );
  }, [user?.peoStaff?.isPartnershipCompany, user?.peoPayrollProviderStaff?.isPartnershipCompany]);

  const updateCurrentUser = React.useCallback(
    async (params: { input: UserUpdate; context?: DefaultContext }) => {
      const { input, context } = params;

      await updateUser({
        variables: {
          where: { id: user?.id },
          data: input,
        },
        context,
      });
    },
    [updateUser, user?.id],
  );

  return {
    user,
    id,
    isUserLoading,
    timezone,
    userFullName,
    isPartnershipCompanyUser,
    updateCurrentUser,
    isUserUpdating,
  };
};
